import React, { useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import PlayGlyph from '@rover/icons/svg/play-glyph.svg';
import { Box, Button, Flex } from '@rover/kibble/core';
import ImageGallery from '@rover/react-lib/src/components/ImageGallery/ImageGallery';
import type {
  GalleryItem,
  GalleryVideoItem,
} from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import {
  GalleryViewMode,
  isGalleryVideoItem,
} from '@rover/react-lib/src/components/ImageGallery/ImageGallery.common';
import insertIntroVideoItem from '@rover/react-lib/src/components/ImageGallery/insertIntroVideoItem';
import useIntroVideoTracking from '@rover/react-lib/src/components/IntroVideo/useIntroVideoTracking';
import { HorizontalLayout } from '@rover/react-lib/src/components/layouts';
import { shouldLazyLoadImage } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import type { SearchResult } from '@rover/types/src/apiResponses/FetchSearchResponse';
import type { PriceTransparencyLevel } from '@rover/types/src/PriceTransparencyLevel';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import AddToShortlistButton from '../../shortlist/components/AddToShortlistButton';
import useFlyToShortlistPreview from '../../shortlist/hooks/useFlyToShortlistPreview';
import useShortlist from '../../shortlist/hooks/useShortlist';

import LegacyPriceColumn from './PriceColumn/LegacyPriceColumn';
import CalendarRecency from './CalendarRecency';
import FeaturedLabel from './FeaturedLabel';
import FeaturedReview from './FeaturedReview';
import HolidayConfirmation from './HolidayConfirmation';
import ImageColumn from './ImageColumn';
import InfoColumn from './InfoColumn';
import PillsAndFavoriteRow from './PillsAndFavoriteRow';
import { SEARCH_RESULT_CARD_Z_INDEX } from './SearchResultCard.constants';

type Props = {
  searchResult: SearchResult;
  displayExpanded: boolean;
  index?: number;
  isSeoPage: boolean;
  showRank: boolean;
  locale: Locale;
  showFromText: boolean;
  isBHUser: boolean;
  shouldShowPriceInCredits: boolean;
  isAuthenticated: boolean | null | undefined;
  onFavoriteToggled: () => void;
  hideFavorite?: boolean;
  preferCatReviews: boolean;
  priceTransparencyLevel?: PriceTransparencyLevel;
  sectionRef: React.RefObject<HTMLElement | null>;
  introVideoDisplayEnabled?: boolean;
  cbsa?: string | null;
};

const LegacyCardContent = ({
  searchResult,
  displayExpanded,
  index,
  isSeoPage,
  showRank,
  locale,
  showFromText,
  isBHUser,
  shouldShowPriceInCredits,
  isAuthenticated,
  onFavoriteToggled,
  hideFavorite,
  preferCatReviews,
  priceTransparencyLevel,
  sectionRef,
  introVideoDisplayEnabled = false,
  cbsa,
}: Props): React.ReactElement => {
  const { t } = useLingui();
  const {
    featuredLabel,
    defaultImage: { small: smallImage, medium: mediumImage },
    shortName,
    primaryBadgeData,
    currencyCode,
    price,
    priceInCredits,
    unitAsSentence,
    isFavorite,
    description,
    catReviewText,
    reviewText,
    catReviewPhotoUrl,
    reviewDogPhotoUrl,
    calendarRecency,
    holidayConfirmation,
    personOpk,
    images,
    introVideoUrl,
    introVideoThumbnail,
  } = searchResult;
  const cardTreatment = SearchCardTreatment.legacy;

  const introVideo = useMemo<GalleryVideoItem | undefined>(() => {
    if (!introVideoDisplayEnabled) return undefined;
    if (!introVideoUrl || !introVideoThumbnail) return undefined;

    return {
      small: introVideoThumbnail,
      largeUncropped: introVideoThumbnail,
      video: { sources: [introVideoUrl], description: t`Intro video` },
    };
  }, [introVideoDisplayEnabled, introVideoUrl, introVideoThumbnail, t]);

  const galleryItems = useMemo<GalleryItem[]>(() => {
    const photos = images?.galleryImages ?? [];
    const totalPhotos = photos.length;

    return (
      insertIntroVideoItem(
        photos.map(({ smallImage: gallerySmallImage, largeUncropped }, photoPosition) => {
          const photoNumber = photoPosition + 1;

          return {
            small: gallerySmallImage,
            largeUncropped,
            altText: t`Photo ${photoNumber} of ${totalPhotos}`,
          };
        }),
        introVideo
      ) ?? []
    );
  }, [images, introVideo, t]);

  const introVideoIndex = galleryItems.findIndex(isGalleryVideoItem);

  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const introVideoTracking = useIntroVideoTracking({
    entryPoint: 'card',
    sitterOpk: personOpk,
    isLightboxOpen,
    cbsa,
  });

  const introVideoPressed = (): void => {
    setLightboxIndex(introVideoIndex);
    setIsLightboxOpen(true);
  };

  const { isVisible: isShortlistVisible } = useShortlist();
  const showAddToShortlist = isShortlistVisible && !isSeoPage;

  const {
    anchorRef: shortlistButtonRef,
    onAdded: handleShortlistAdded,
    flyingAvatar,
  } = useFlyToShortlistPreview(mediumImage || smallImage);

  return (
    // Font size is overridden here to cancel out layout shifting during final font loading
    <Box ref={sectionRef} sx={{ fontSize: '15px' }}>
      {featuredLabel && <FeaturedLabel featuredLabel={featuredLabel} />}
      <HorizontalLayout noWrap data-testid="legacy-card-content">
        <ImageColumn
          displayExpanded={displayExpanded}
          desktopProfileImage={mediumImage}
          mobileProfileImage={smallImage}
          shortName={shortName}
          lazy={shouldLazyLoadImage(index)}
          badge={primaryBadgeData}
          cardTreatment={cardTreatment}
        />
        <InfoColumn
          displayExpanded={displayExpanded}
          index={index}
          searchResult={searchResult}
          isSeoPage={isSeoPage}
          showRank={showRank}
          hideHeadline={false}
        />
        <LegacyPriceColumn
          currencyCode={currencyCode}
          price={price}
          priceInCredits={priceInCredits}
          shouldShowPriceInCredits={shouldShowPriceInCredits}
          showFromText={showFromText}
          locale={locale}
          unitAsSentence={unitAsSentence}
          isTrainingDesign={false}
          isBHUser={isBHUser}
          priceTransparencyLevel={priceTransparencyLevel}
        />
      </HorizontalLayout>
      <PillsAndFavoriteRow
        isAuthenticated={isAuthenticated}
        isFavorite={isFavorite}
        onFavoriteToggled={onFavoriteToggled}
        ratingsAverage={searchResult.ratingsAverage}
        repeatClientCount={searchResult.providerProfile.repeatClientCount}
        reviewsCount={searchResult.reviewsCount}
        yearsOfExperience={searchResult.yearsOfExperience}
        hideFavorite={hideFavorite || showAddToShortlist}
      />
      <FeaturedReview
        description={description}
        reviewText={preferCatReviews && catReviewText ? catReviewText : reviewText}
        reviewPetPhotoUrl={
          preferCatReviews && catReviewPhotoUrl ? catReviewPhotoUrl : reviewDogPhotoUrl
        }
      />
      {(calendarRecency && (
        <Flex justifyContent="space-between" width="100%" flexWrap="wrap" gap="3x">
          <CalendarRecency calendarRecency={calendarRecency} />
          {holidayConfirmation && <HolidayConfirmation holidayConfirmation={holidayConfirmation} />}
        </Flex>
      )) ||
        (holidayConfirmation && (
          <Flex justifyContent="space-between" width="100%" flexWrap="wrap" gap="3x">
            <HolidayConfirmation holidayConfirmation={holidayConfirmation} />
          </Flex>
        ))}
      {!!introVideo && (
        <Box mt="3x" position="relative" zIndex={`${SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE}`}>
          <Button
            icon={PlayGlyph}
            onPress={introVideoPressed}
            width={['100%', 'auto']}
            data-testid="legacy-card-intro-video-button"
            webProps={{ 'aria-label': t`Intro video from ${shortName}` }}
          >
            {t`Intro video`}
          </Button>
        </Box>
      )}
      {isLightboxOpen && (
        <ImageGallery
          images={galleryItems}
          galleryViewMode={GalleryViewMode.Lightbox}
          lightboxIndex={lightboxIndex}
          setLightboxIndex={setLightboxIndex}
          onClose={() => setIsLightboxOpen(false)}
          onImageClick={setLightboxIndex}
          {...introVideoTracking}
        />
      )}
      {showAddToShortlist && (
        // Sits above the card-wide profile anchor so the toggle is clickable.
        <Box
          ref={shortlistButtonRef}
          mt="3x"
          position="relative"
          zIndex={`${SEARCH_RESULT_CARD_Z_INDEX.INTERACTIVE}`}
        >
          <AddToShortlistButton
            personOpk={personOpk}
            source="search"
            width={['100%', 'auto']}
            onAdded={handleShortlistAdded}
          />
        </Box>
      )}
      {flyingAvatar}
    </Box>
  );
};

export default LegacyCardContent;
